import type { JSX } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api-client';
import { KNOWLEDGE_BASE, isFeatureEnabled } from '@/auth/features';
import type {
  Binding,
  Segment,
  Provider,
  Model,
  Skill,
  Mcp,
  KnowledgeBase,
  ResourceType,
} from '@echo-work-platform/shared-types';

// Providers are not bindable — binding a model implicitly unlocks its provider.
const RESOURCE_TYPES: { type: ResourceType; label: string; endpoint: string; feature?: string }[] = [
  { type: 'MODEL', label: 'Models', endpoint: '/api/admin/models-flat' },
  { type: 'SKILL', label: 'Agents', endpoint: '/api/admin/skills' },
  { type: 'MCP', label: 'Integrations', endpoint: '/api/admin/mcps' },
  {
    type: 'KNOWLEDGE_BASE',
    label: 'KBs',
    endpoint: '/api/admin/knowledge-bases',
    feature: KNOWLEDGE_BASE,
  },
];

interface Named {
  id: string;
  name: string;
  inactive?: boolean;
}

export function Bindings(): JSX.Element {
  const queryClient = useQueryClient();
  const knowledgeBaseServed = isFeatureEnabled(KNOWLEDGE_BASE);
  const segments = useQuery({
    queryKey: ['/api/admin/segments'],
    queryFn: () => api.get<Segment[]>('/api/admin/segments'),
  });
  const skills = useQuery({
    queryKey: ['/api/admin/skills'],
    queryFn: () => api.get<Skill[]>('/api/admin/skills'),
  });
  const mcps = useQuery({ queryKey: ['/api/admin/mcps'], queryFn: () => api.get<Mcp[]>('/api/admin/mcps') });
  const kbs = useQuery({
    queryKey: ['/api/admin/knowledge-bases'],
    queryFn: () => api.get<KnowledgeBase[]>('/api/admin/knowledge-bases'),
    enabled: knowledgeBaseServed,
  });
  const bindings = useQuery({
    queryKey: ['/api/admin/bindings'],
    queryFn: () => api.get<Binding[]>('/api/admin/bindings'),
  });

  // Fetch all models across providers
  const modelsByProv = useQuery({
    queryKey: ['/api/admin/models-flat'],
    queryFn: async () => {
      const provs = await api.get<Provider[]>('/api/admin/providers');
      const out: Model[] = [];
      for (const p of provs) {
        const ms = await api.get<Model[]>(`/api/admin/providers/${p.id}/models`);
        out.push(...ms);
      }
      return out;
    },
  });

  const create = useMutation({
    mutationFn: (v: { segmentId: string; resourceType: ResourceType; resourceId: string }) =>
      api.post('/api/admin/bindings', v),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/bindings'] }),
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/api/admin/bindings/${id}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['/api/admin/bindings'] }),
  });

  const resourcesOf = (t: ResourceType): Named[] => {
    switch (t) {
      // Bindings survive deactivation (EW-191): an inactive model stays bindable, and its
      // existing bindings must stay manageable here — mark it instead of hiding it, or a
      // deactivated model's bindings could never be removed before a delete.
      case 'MODEL':
        return (modelsByProv.data ?? []).map((m) => ({ id: m.id, name: m.name, inactive: !m.isActive }));
      case 'SKILL':
        return skills.data ?? [];
      case 'MCP':
        return mcps.data ?? [];
      case 'KNOWLEDGE_BASE':
        return kbs.data ?? [];
    }
  };

  const hasBinding = (segmentId: string, type: ResourceType, resourceId: string): Binding | undefined =>
    bindings.data?.find(
      (b) => b.segmentId === segmentId && b.resourceType === type && b.resourceId === resourceId,
    );

  const segs = segments.data ?? [];
  const resourceTypes = RESOURCE_TYPES.filter(
    ({ feature }) => feature === undefined || isFeatureEnabled(feature),
  );

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-semibold">Bindings (Segment × Resource)</h2>
      {resourceTypes.map(({ type, label }) => {
        const rs = resourcesOf(type);
        return (
          <div key={type} className="card overflow-x-auto">
            <div className="border-b border-slate-200 px-4 py-2 text-sm font-semibold">{label}</div>
            <table className="w-full table-fixed text-sm">
              <colgroup>
                <col className="w-56" />
                {segs.map((s) => (
                  <col key={s.id} className="w-28" />
                ))}
              </colgroup>
              <thead className="bg-slate-50 text-xs text-slate-500">
                <tr className="divide-x divide-slate-200">
                  <th className="px-4 py-2 text-left font-medium">Resource</th>
                  {segs.map((s) => (
                    <th key={s.id} className="px-2 py-2 text-center font-medium">
                      <span className="block truncate" title={s.name}>
                        {s.name}
                        {s.isDefault && ' ⭐'}
                      </span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rs.map((r) => (
                  <tr key={r.id} className="divide-x divide-slate-100 hover:bg-slate-50/60">
                    <td
                      className={`truncate px-4 py-2 font-medium ${r.inactive ? 'text-slate-400' : 'text-slate-700'}`}
                      title={r.inactive ? `${r.name} (inaktív)` : r.name}
                    >
                      {r.name}
                      {r.inactive && <span className="ml-1 text-xs font-normal">(inaktív)</span>}
                    </td>
                    {segs.map((s) => {
                      const b = hasBinding(s.id, type, r.id);
                      return (
                        <td key={s.id} className="px-2 py-2 text-center">
                          <input
                            type="checkbox"
                            className="h-4 w-4 cursor-pointer accent-brand-600"
                            checked={!!b}
                            onChange={(e) => {
                              if (e.target.checked) {
                                create.mutate({ segmentId: s.id, resourceType: type, resourceId: r.id });
                              } else if (b) {
                                remove.mutate(b.id);
                              }
                            }}
                          />
                        </td>
                      );
                    })}
                  </tr>
                ))}
                {rs.length === 0 && (
                  <tr>
                    <td colSpan={segs.length + 1} className="px-4 py-4 text-center text-slate-400">
                      Nincs {label.toLowerCase()}.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );
      })}
    </div>
  );
}
